<template>
  <v-container fluid>
    <h2>{{ $t('bulk_create_users') }}</h2>
    <v-card>
      <v-card-text>
        <v-form id="bulk-user-form" @submit.prevent="onSubmit" autocomplete="off" name="lastpass-disable-search"
                :novalidate="true">
          <v-row>
            <v-col xl="8" lg="8">
              <v-textarea
                v-model="emails"
                :label="$tc('email_address', 2)"
                placeholder="bob@example.com"
                :error-messages="emailErrors"
                outlined />
            </v-col>
            <v-col>
              <p class="small">{{ $t('bulk_create_users_instructions') }}</p>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-switch
                v-model="sendEmail"
                :label="$t('send_account_setup_email')" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <vf-autocomplete
                v-model="selectedCustomers"
                :items="customers"
                item-text="name"
                item-value="id"
                :error-messages="customerErrors"
                :label="$t('customer_assignment')" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-btn
                type="submit"
                color="primary" >
                <v-icon class="ml-n2">mdi-plus</v-icon>
                {{ $t('create') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>

import VfAutocomplete from '@/components/shared/VfAutocomplete'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import { email } from 'vuelidate/lib/validators'
import { split, map, trim, isEmpty, get } from 'lodash'

export default {
  name: 'BulkUserCreate',
  components: {
    VfAutocomplete
  },
  mixins: [
    BrowserUtilitiesMixin,
    CurrentUserMixin,
    ValidationMixin
  ],
  data () {
    return {
      loading: false,
      selectedCustomers: [],
      emails: null,
      parsedEmails: [],
      sendEmail: true
    }
  },
  validations: {
    parsedEmails: {
      required: function (value) {
        return !isEmpty(value)
      },
      $each: {
        email
      }
    },
    selectedCustomers: {
      required: function (value) {
        return !isEmpty(value)
      }
    }
  },
  computed: {
    emailErrors () {
      if (this.$v.parsedEmails.required) {
        return map(this.$v.parsedEmails.$each.$iter, validation => {
          if (get(validation, '$error') === true) {
            return this.$t('invalid_email', { email: validation.$model })
          }
        })
      } else {
        return this.validationErrors([{
          prop: this.$v.parsedEmails,
          rule: 'required',
          message: this.$t('required')
        }])
      }
    },
    customerErrors () {
      return this.validationErrors([{
        prop: this.$v.selectedCustomers,
        rule: 'required',
        message: this.$t('required')
      }])
    }
  },
  methods: {
    /**
     * Submit the Location form.
     */
    onSubmit () {
      this.parsedEmails = this.parseEmails(this.emails)

      this.$v.$touch()
      if (this.$v.$invalid) return false

      this.loading = true

      const json = {
        emails: this.parsedEmails,
        customers: this.selectedCustomers,
        sendEmail: this.sendEmail
      }

      this.$httpAdapter({
        url: this.$orm('User').endpoint + '/bulk-create',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(json)
      }).then(() => {
        this.$router.push('/users', () => {
          this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            `${this.$i18n.tc('user', 2)} ${this.$i18n.t('generic_save_success')}`
          )
        })
      }).catch(error => {
        this.handleError(error)
      }).finally(() => {
        this.loading = false
      })
    },
    /**
     * Parse emails from raw string.
     * Handles either newline separated or comma separated emails.
     * @param emails
     * @returns {Array}
     */
    parseEmails (emails) {
      let parsed = split(emails, '\n')
      if (parsed.length === 1) {
        parsed = split(emails, ',')
      }
      parsed = map(parsed, n => {
        return trim(n)
      })
      // lodash splitting an empty or null string always gives you an array with one blank entry.
      if (parsed.length > 0 && parsed[0] !== '') {
        return parsed
      } else {
        // Returning a blank array to make it easier to validate.
        return []
      }
    }
  },
  mounted () {
    this.updateBrowserTitle(this.$t('create_user'))
  }
}

</script>

<style scoped lang="scss">

// Making the placeholder a bit lighter than standard because it was a little confusing.
textarea#emails::placeholder {
  color: gray;
}

</style>
